// /ai1, /ai2 (11 §1): שיחה + פאנל צד. דסקטופ ראשון: רשימת שיחות | שיחה | פאנל; במובייל — שיחה במסך מלא,
// הרשימה והפאנל כ-drawer. web-ai-stage-layout: השיחה מקבלת את המסך — בלי תקרת רוחב, הרשימה מתקפלת לפס כשיש שיחה פתוחה,
// הפאנל מתקפל לפס כשאין מה שמחכה לאדם (נפתח מעצמו כשיש); פס = sheet בלחיצה, לא שינוי פריסה. הבלוקים בתשובה ברוחב השיחה,
// "הגדל" מעביר בלוק למסך מלא עם שדה הכתיבה בתחתית; אייבי פותחת הגדלה רק כשביקשו (focus:'expand', אירוע חי בלבד). ה-RBAC מהשרת: הפאנל נטען ראשון, ו-403 שלו הוא מה שהמסך מציג (11 §5.2).
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import type { ExpandHost } from '@aibify/display/client';
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { aiApi } from '../lib/aiApi.ts';
import { UPLOAD_ACCEPT, UPLOAD_LIMITS, type ConversationSummary } from '../lib/aiContract.ts';
import { useSession } from '../lib/auth.ts';
import { isLive, useConversation } from '../lib/conversation.ts';
import { T } from '../lib/strings.ts';
import { fmtWhen } from '../lib/time.ts';
import { cn } from '../lib/utils.ts';
import { useIsDesktop } from '../lib/media.ts';
import { convMemory } from '../lib/convMemory.ts';
import { ErrorView, Loading } from '../components/Status.tsx';
import { Button } from '../components/ui/button.tsx';
import { Sheet } from '../components/ui/sheet.tsx';
import { useOpenConsent } from '../components/ConsentDrawer.tsx';
import { useMailConnect, useOnMailConnected } from '../lib/mailConnect.ts';
import { blockKey, OutgoingBubble, TurnBubble } from '../components/ai/TurnView.tsx';
import { Composer } from '../components/ai/Composer.tsx';
import { IconList, IconPanel, IconPlus } from '../components/brand/icons.tsx';
import { Ai1Panel, Ai2Panel, ai1Waiting } from '../components/ai/Panels.tsx';
import { JumpToEnd, useStickToBottom } from '../components/ai/StickToBottom.tsx';
import { TurnActions } from '../components/ai/TurnActions.tsx';
import { type PendingFile } from '../components/ai/Attachments.tsx';

const ACCEPT_EXT = new Set(UPLOAD_ACCEPT.split(',').map((e) => e.slice(1)));
let fileKey = 0;

/** web-conversation-list-search: חיפוש — מקומי על הכותרות מיד, ובשרת (כותרת + ההודעות של המשתמש עצמו) אחרי 300ms. */
function useConversationSearch(level: 1 | 2, q: string) {
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const term = debounced.trim();
  return useQuery({
    queryKey: ['conversations', 'search', level, term],
    queryFn: () => aiApi.searchConversations(term, level),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });
}

/** המילה המבוקשת מודגשת בתוך שורת ההקשר. */
function Highlight({ text, q }: { text: string; q: string }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-transparent font-semibold text-foreground underline decoration-primary decoration-2 underline-offset-2">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

/**
 * רשימת השיחות. variant 'rail' (הרשימה הקבועה בדסקטופ, בלי שיחה פתוחה) — כותרת בשורה אחת חתוכה, בכוונה: המקום של השיחה.
 * 'drawer' (sheet מתוך שיחה ובמובייל) — באים לקרוא: הכותרת נשברת עד 3 שורות, המלאה ב-title.
 */
function ConversationList({ level, current, variant }: { level: 1 | 2; current?: string; variant: 'rail' | 'drawer' }) {
  const q = useQuery({ queryKey: ['conversations'], queryFn: aiApi.listConversations });
  const [query, setQuery] = useState('');
  const term = query.trim();
  const remote = useConversationSearch(level, query);
  const all = q.data?.conversations.filter((c) => c.level === level) ?? [];
  const local = term ? all.filter((c) => (c.titleHe ?? '').toLowerCase().includes(term.toLowerCase())) : all;
  const seen = new Set(local.map((c) => c.id));
  const list: ConversationSummary[] = term.length >= 2 ? [...local, ...(remote.data?.conversations.filter((c) => !seen.has(c.id)) ?? [])] : local;
  return (
    <nav aria-label={T.ai.conversations} className="flex flex-col gap-2" data-testid="conversation-list" data-variant={variant}>
      <NavLink to={`/ai${level}`} end className="inline-flex min-h-11 items-center justify-center rounded-sm bg-action px-3 font-semibold text-action-foreground hover:bg-action-hover" data-testid="new-conversation">
        {T.ai.newConversation}
      </NavLink>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={T.ai.searchConversations}
        aria-label={T.ai.searchConversations}
        className="min-h-11 w-full rounded-sm border border-input/70 bg-card px-3 text-base text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
        data-testid="conversation-search"
      />
      {q.isError && <ErrorView error={q.error} onRetry={() => q.refetch()} />}
      {q.data && !all.length && <p className="m-0 text-sm text-muted-foreground">{T.ai.noConversations}</p>}
      {q.data && !!all.length && term && !list.length && !(term.length >= 2 && remote.isFetching) && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="conversation-search-empty">{T.ai.noSearchResults}</p>
      )}
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {list.map((c) => (
          <li key={c.id}>
            <NavLink
              to={`/ai${level}/${c.id}`}
              className={cn('flex min-h-11 flex-col justify-center rounded-sm px-2 py-1 text-sm hover:bg-muted', c.id === current && 'bg-primary-soft text-primary')}
              data-testid="conversation-item"
            >
              <span className={cn('font-medium', variant === 'rail' ? 'truncate' : 'line-clamp-3 break-words')} title={c.titleHe ?? undefined}>
                {c.titleHe ?? T.ai.untitled}
              </span>
              {c.snippetHe && (
                <span className={cn('text-xs text-muted-foreground', variant === 'rail' ? 'truncate' : 'line-clamp-2')} data-testid="conversation-snippet">
                  <Highlight text={c.snippetHe} q={term} />
                </span>
              )}
              <bdi dir="auto" className="text-xs text-muted-foreground">
                {fmtWhen(c.lastActiveAt)}
              </bdi>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function AiPage({ level }: { level: 1 | 2 }) {
  const { conversationId } = useParams();
  const nav = useNavigate();
  const s = useSession();
  const qc = useQueryClient();
  const desktop = useIsDesktop();
  const [listOpen, setListOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [search] = useSearchParams();
  // "פתח בבינה-2" משדה "שאל" (11): השאלה מגיעה כ-?q= וממלאת את השדה (לא נשלחת לבד)
  // web-screen-budget: הטיוטה נשמרת לכל שיחה ("פתח" ליצירה ו"חזור" — מה שהוקלד עדיין בשדה); נמחקת בשליחה
  const [text, setText] = useState(() => search.get('q') ?? (conversationId ? convMemory.readDraft(conversationId) : null) ?? '');
  const typeText = (t: string) => {
    setText(t);
    if (conversationId) convMemory.saveDraft(conversationId, t);
  };
  // הודעה באמצע תור (10 §1): השדה פתוח גם כשהסוכן עובד — Enter שולח מיד לתור החי. ההודעה עוברת מיד לתמליל (בועה
  // ממתינה של תיבת היציאה); מה שלא ניתן לשלוח עכשיו (409) ממתין שם ונשלח כשהתור מסתיים, כשל — שם, עם "שליחה שוב"
  // קבצים מצורפים (10 §1): נבחרים (כפתור/גרירה/הדבקה/מצלמה במובייל) ומוחזקים כצ'יפים; מועלים רק בשליחה, עם התקדמות
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [dragging, setDragging] = useState(0);
  const stick = useStickToBottom();
  // web-screen-budget הכרעה 7: הערך השמור של השיחה נקרא כבר ברינדור הראשון שלה (תמליל מהמטמון מצויר מיד)
  const restoreTo = useRef<number | null>(null);
  const restoreFor = useRef<string | undefined>(undefined);
  if (restoreFor.current !== conversationId) {
    restoreFor.current = conversationId;
    restoreTo.current = conversationId ? convMemory.readScroll(conversationId) : null;
  }
  const openConsent = useOpenConsent();
  const openMail = useMailConnect();
  // בינה-2: הפאנל לפי השיחה (מה בנינו / ממתין לך / התצוגה המקדימה שלה); מעבר שיחה לא מחזיר "טוען" לכל המסך
  const panel = useQuery({
    queryKey: level === 1 ? ['panel', 1] : ['panel', 2, conversationId ?? null],
    queryFn: (): Promise<unknown> => (level === 1 ? aiApi.panelAi1() : aiApi.panelAi2(conversationId)),
    // הנתונים הקודמים נשארים רק באותה רמה: צורת הפאנל של מנהל בינה אינה של אייבי — להציג אותם ב-Ai2Panel מפיל את המסך
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === level ? prev : undefined),
  });
  const conv = useConversation(level, conversationId, {
    onCreated: (id) => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      nav(`/ai${level}/${id}`);
    },
    onConsentPending: openConsent,
    // 20: אייבי צריכה Gmail עכשיו — "חיבור Gmail" נפתח מעצמו; הסוכן ממתין לחיבור ומנסה שוב (המשך אמיתי של התור)
    onMailConnect: (caps) => openMail(caps),
    onConsentDecided: (id) => {
      void qc.invalidateQueries({ queryKey: ['consent', id] });
      void qc.invalidateQueries({ queryKey: ['consents'] });
    },
    // תוצאת כלי משנה את הפאנל (בינה-1: קוד צימוד, הצעה, סנכרון; בינה-2: יצירה/פריסה שנבנתה) — רענון מיד, לא רק בסוף התור
    onToolResult: () => {
      void qc.invalidateQueries({ queryKey: ['panel', level] });
      void qc.invalidateQueries({ queryKey: ['consents'] }); // התג של "אישורים ממתינים" בפס
    },
    // אייבי ביקשה להגדיל (המשתמש אמר "תגדיל") — הבלוק שהגיע עכשיו נפתח בהגדלה (אחרי שהוא בתמליל: האפקט למטה)
    onExpandBlock: () => {
      wantExpand.current = true;
      setExpandTick((n) => n + 1);
    },
    onTurnDone: () => {
      void qc.invalidateQueries({ queryKey: ['panel', level] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
  const live = conv.turns.find((t) => t.status === 'queued' || t.status === 'running' || t.status === 'stopping');
  // שחזור הגלילה כשהתמליל נטען: תור חי — ההצמדה לסוף גוברת; תוכן שעוד לא הגיע לגובה (גרף שמצויר מאוחר) — עוד ניסיון כשהוא גדל
  const restoring = restoreTo.current !== null && !conv.loading && conv.turns.length > 0;
  useLayoutEffect(() => {
    if (!restoring) return;
    if (live) {
      restoreTo.current = null;
      stick.toEnd();
      return;
    }
    const attempt = () => {
      const want = restoreTo.current;
      if (want === null) return true;
      stick.release();
      window.scrollTo({ top: want, behavior: 'instant' });
      if (document.documentElement.scrollHeight - window.innerHeight < want - 2) return false;
      restoreTo.current = null;
      return true;
    };
    if (attempt()) return;
    const ro = new ResizeObserver(() => attempt() && ro.disconnect());
    ro.observe(document.body);
    const give = setTimeout(() => {
      ro.disconnect();
      restoreTo.current = null;
    }, 3000);
    return () => {
      ro.disconnect();
      clearTimeout(give);
    };
  }, [restoring, !!live]);
  // 20: תור חי שמחכה ל-Gmail — אחרי החיבור הוא ממשיך בעצמו (ה-drawer אומר "ממשיכים", לא "נסה שוב")
  useOnMailConnected(() => undefined, !!live?.parts.some((p) => p.kind === 'mail_connect'));
  // הכפתורים שאחרי התור האחרון (15 "להמשך") — תנאי ההצגה בתוך TurnActions
  const lastTurn = conv.turns.at(-1);

  // הגדלה מתוך השיחה: מפתח הבלוק המוגדל (turnId:partIndex). תמונת מצב ברגע הפתיחה — כמה תורים הסתיימו — ל"תשובה חדשה · הצג"
  const [expanded, setExpanded] = useState<string | null>(null);
  const doneCount = conv.turns.filter((t) => !isLive(t)).length;
  const snap = useRef(0);
  const wantExpand = useRef(false);
  const [expandTick, setExpandTick] = useState(0);
  const lastBlockKey = (() => {
    for (let ti = conv.turns.length - 1; ti >= 0; ti--) {
      const t = conv.turns[ti]!;
      for (let i = t.parts.length - 1; i >= 0; i--) if (t.parts[i]!.kind === 'block') return blockKey(t.id, i);
    }
    return null;
  })();
  const openExpand = (key: string | null) => {
    if (key) snap.current = doneCount;
    setExpanded(key);
  };
  useEffect(() => {
    if (!wantExpand.current || !lastBlockKey) return;
    wantExpand.current = false;
    snap.current = doneCount;
    setExpanded(lastBlockKey);
  }, [expandTick, lastBlockKey]);

  useEffect(() => {
    setListOpen(false);
    setExpanded(null);
  }, [conversationId]);

  // מעבר בין שיחות קיימות: השדה מקבל את הטיוטה של השיחה החדשה (שיחה חדשה שנוצרה עכשיו — הטקסט נשאר כמו שהוא)
  const prevConv = useRef(conversationId);
  useEffect(() => {
    const prev = prevConv.current;
    prevConv.current = conversationId;
    if (prev && conversationId && prev !== conversationId) setText(convMemory.readDraft(conversationId) ?? '');
  }, [conversationId]);

  // web-screen-budget הכרעה 7: מקום הגלילה לכל שיחה — נשמר ביציאה מהשיחה, מוחזר כשחוזרים (לפני ההצמדה לסוף, שגוברת רק
  // בלי ערך שמור או כשיש תור חי). עד שהשחזור הצליח (התוכן גבוה מספיק) — לא דורסים את הערך השמור.
  useLayoutEffect(() => {
    if (!conversationId) return;
    if (restoreTo.current !== null) stick.release();
    let pos = -1;
    const onScroll = () => {
      if (restoreTo.current === null) pos = window.scrollY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (restoreTo.current === null && pos >= 0) convMemory.saveScroll(conversationId, pos);
    };
  }, [conversationId]);

  const addFiles = (list: Iterable<File>) => {
    setPending((cur) => {
      const next = [...cur];
      for (const file of list) {
        const ext = file.name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '';
        const errorHe = next.length >= UPLOAD_LIMITS.perMessage ? T.ai.attach.tooMany : file.size > UPLOAD_LIMITS.fileBytes ? T.ai.attach.tooLarge : !ACCEPT_EXT.has(ext) ? T.ai.attach.unsupported(file.name) : null;
        const preview = !errorHe && /^image\/(png|jpeg|gif|webp)$/.test(file.type) ? URL.createObjectURL(file) : null;
        next.push({ key: `f${++fileKey}`, file, preview, progress: null, errorHe });
      }
      return next;
    });
  };
  const removeFile = (key: string) =>
    setPending((cur) => {
      const f = cur.find((x) => x.key === key);
      if (f?.preview) URL.revokeObjectURL(f.preview);
      return cur.filter((x) => x.key !== key);
    });
  const hasFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes('Files');
  const onDragEnter = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragging((n) => n + 1);
  };
  const onDragLeave = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    setDragging((n) => Math.max(0, n - 1));
  };
  const onDrop = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragging(0);
    addFiles(e.dataTransfer.files);
  };

  const submit = () => {
    const t = text;
    const files = pending.filter((f) => !f.errorHe);
    const keys = new Set(files.map((f) => f.key));
    if (!t.trim() && !files.length) return;
    if (conv.busy) return; // עוצרים — השדה נעול ממילא
    if (conversationId) convMemory.saveDraft(conversationId, '');
    // ההודעה עוברת לתמליל מיד (בועה ממתינה); השדה מתפנה להודעה הבאה. הקבצים הפגומים נשארים בצ'יפים
    conv.send(
      t,
      files.map((f) => f.file),
    );
    setText('');
    for (const f of files) if (f.preview) URL.revokeObjectURL(f.preview);
    setPending((cur) => cur.filter((f) => !keys.has(f.key)));
    stick.toEnd(); // שליחה תמיד גוללת לסוף ומצמידה מחדש
  };
  // "עריכה"/"ביטול" של הודעה ממתינה/שנכשלה: הטקסט והקבצים חוזרים לשדה (מה שכבר כתוב בו נשמר — הטקסט החוזר לפניו)
  const takeBack = (localId: string) => {
    const back = conv.takeBack(localId);
    if (!back) return;
    setText((cur) => (cur.trim() ? `${back.text}\n${cur}` : back.text));
    if (back.files.length) addFiles(back.files);
  };
  // כפתור "להמשך": ה-prompt נשלח כהודעת המשתמש (כשל — נשאר בתמליל עם "שליחה שוב"/"עריכה")
  const pickNextStep = (prompt: string) => {
    if (conv.busy || conv.live) return;
    stick.toEnd();
    conv.send(prompt);
  };
  // מספר הפריטים שמחכים לאדם (בינה-1: מה שמוצג תחת "ממתין לך" — ai1Waiting, מקור אחד למונה ולפאנל; בינה-2: "ממתין לך" + התראות תזמון פתוחות, 19 §1.6) — תג על הפאנל/הפס
  const p = (panel.data ?? {}) as { waiting?: { consents: unknown[]; drafts: unknown[]; failedRuns: unknown[] }; alerts?: unknown[] };
  const attention =
    level === 1 ? ai1Waiting(panel.data as Parameters<typeof ai1Waiting>[0]).count : (p.waiting?.consents?.length ?? 0) + (p.waiting?.drafts?.length ?? 0) + (p.waiting?.failedRuns?.length ?? 0) + (p.alerts?.length ?? 0);
  // קיפול (דסקטופ): הרשימה — פס כשיש שיחה פתוחה; הפאנל — פס כשאין מה שמחכה לאדם (נפתח מעצמו כשיש). פס נפתח כ-sheet
  const listRail = desktop && !!conversationId;
  const panelRail = desktop && attention === 0;
  // הפאנל נפתח לעמודה (פריט ממתין) — ה-sheet שלו נסגר (אין שני פאנלים)
  useEffect(() => {
    if (desktop && !panelRail) setPanelOpen(false);
  }, [desktop, panelRail]);

  // ההחזרות המוקדמות אחרי הגדרת submit: האפקט של ההודעה הממתינה קורא ל-submit גם כשהפאנל עוד נטען
  if (panel.isPending) return <Loading />;
  if (panel.isError) return <ErrorView error={panel.error} onRetry={() => panel.refetch()} />;

  const panelBody =
    level === 1 ? <Ai1Panel data={panel.data as Parameters<typeof Ai1Panel>[0]['data']} /> : <Ai2Panel data={panel.data as Parameters<typeof Ai2Panel>[0]['data']} />;
  const title = level === 1 ? T.ai.title1 : T.ai.title2;

  // שיחה ריקה (web-visual-identity): השדה במרכז האנכי עם הצעות פתיחה; ההודעה הראשונה מורידה אותו לתחתית
  const empty = !conversationId && !conv.loading && conv.turns.length === 0 && conv.outbox.length === 0;
  const composer = (inExpand: boolean, centered = false) => (
    <Composer
      centered={centered}
      level={level}
      text={text}
      setText={typeText}
      pending={pending}
      onFiles={addFiles}
      onRemoveFile={removeFile}
      onSubmit={submit}
      onStop={() => void conv.stop()}
      live={!!live}
      stopping={live?.status === 'stopping'}
      busy={conv.busy}
      stream={conv.stream}
      showStream={!!conversationId}
      inExpand={inExpand}
      jump={inExpand ? undefined : <JumpToEnd show={!!live && !stick.atEnd} onClick={stick.toEnd} className="absolute bottom-full mb-3" />}
    />
  );
  // "תשובה חדשה · הצג": תור הסתיים מאז שההגדלה נפתחה — ההגדלה לא נסגרת לבד; הצ'יפ סוגר וגולל לתור
  const newReply =
    expanded && doneCount > snap.current ? (
      <span role="status" className="ms-auto">
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setExpanded(null);
            requestAnimationFrame(() => stick.toEnd());
          }}
          data-testid="expand-new-reply"
        >
          {T.ai.newReply}
        </Button>
      </span>
    ) : undefined;
  const expandHost = (key: string): ExpandHost =>
    expanded === key
      ? { open: true, onOpenChange: (o) => openExpand(o ? key : null), footer: conv.canContinue ? composer(true) : undefined, chips: newReply }
      : { open: false, onOpenChange: (o) => openExpand(o ? key : null) };

  const railBtn = 'relative inline-flex size-11 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';
  const attentionBadge =
    attention > 0 ? (
      <span className="rounded-sm bg-action px-1.5 text-xs font-semibold tabular-nums text-action-foreground" data-testid="panel-attention" aria-hidden="true">
        {attention}
      </span>
    ) : null;
  // הרשת: רשימה (13rem / פס) | השיחה — כל השאר | פאנל (22rem / פס). בלי תקרת רוחב (Layout)
  const cols = listRail
    ? panelRail
      ? 'md:grid-cols-[2.75rem_minmax(0,1fr)_2.75rem]'
      : 'md:grid-cols-[2.75rem_minmax(0,1fr)_20rem] lg:grid-cols-[2.75rem_minmax(0,1fr)_22rem]'
    : panelRail
      ? 'md:grid-cols-[12rem_minmax(0,1fr)_2.75rem] lg:grid-cols-[13rem_minmax(0,1fr)_2.75rem]'
      : 'md:grid-cols-[12rem_minmax(0,1fr)_20rem] lg:grid-cols-[13rem_minmax(0,1fr)_22rem]';

  return (
    <div className={cn('flex flex-col gap-3 md:grid md:items-start md:gap-5', cols)} data-testid="ai-page" data-level={level}>
      {desktop ? (
        listRail ? (
          <nav aria-label={T.ai.conversations} className="sticky top-4 flex flex-col items-center gap-2" data-testid="list-rail">
            <NavLink
              to={`/ai${level}`}
              end
              aria-label={T.ai.newConversation}
              title={T.ai.newConversation}
              className={cn(railBtn, 'bg-action text-action-foreground hover:bg-action-hover hover:text-action-foreground')}
              data-testid="new-conversation"
            >
              <IconPlus size={20} />
            </NavLink>
            <button type="button" onClick={() => setListOpen(true)} aria-label={T.ai.openConversations} title={T.ai.conversations} aria-expanded={listOpen} className={railBtn} data-testid="open-conversations">
              <IconList size={20} />
            </button>
          </nav>
        ) : (
          <aside className="sticky top-4">
            <ConversationList level={level} current={conversationId} variant="rail" />
          </aside>
        )
      ) : (
        <div className="flex items-center gap-2">
          <h1 className="m-0 me-auto text-xl font-bold">{title}</h1>
          <Button variant="outline" size="sm" onClick={() => setListOpen(true)} data-testid="open-conversations">
            {T.ai.conversations}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPanelOpen(true)} data-testid="open-panel" aria-label={attention ? `${T.ai.panel} · ${T.ai.attention(attention)}` : undefined}>
            {T.ai.panel}
            {attentionBadge && <span className="ms-1 inline-flex">{attentionBadge}</span>}
          </Button>
        </div>
      )}

      <section
        ref={stick.contentRef}
        className="relative flex min-w-0 flex-col gap-3 md:min-h-[calc(var(--ab-screen)-2rem)]"
        aria-label={title}
        onDragEnter={onDragEnter}
        onDragOver={(e) => hasFiles(e) && e.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="transcript"
      >
        {dragging > 0 && conv.canContinue && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary-soft/80 text-lg font-semibold text-primary" data-testid="drop-overlay">
            {T.ai.attach.dropHere}
          </div>
        )}
        {desktop && <h1 className={cn('m-0 text-2xl font-bold', empty && 'sr-only')}>{title}</h1>}
        {conv.loading && <Loading />}
        {conv.error != null && <ErrorView error={conv.error} />}
        <ol className="m-0 flex list-none flex-col gap-4 p-0" data-testid="turns">
          {conv.turns.map((t) => (
            <TurnBubble
              key={t.id}
              turn={t}
              level={level}
              isAdmin={s?.user.role === 'admin'}
              secrets={conv.secrets}
              followedUp={conv.turns.some((x) => x.followUpOf === t.id)}
              isLast={t === lastTurn}
              onResend={conv.canContinue ? (text) => conv.send(text) : undefined}
              expandHost={expandHost}
            />
          ))}
        </ol>
        {conv.outbox.length > 0 && (
          <ol className="m-0 flex list-none flex-col gap-2 p-0" data-testid="outbox" aria-live="polite">
            {conv.outbox.map((m) => (
              <OutgoingBubble key={m.localId} m={m} onRetry={() => conv.retry(m.localId)} onTakeBack={() => takeBack(m.localId)} />
            ))}
          </ol>
        )}
        <TurnActions
          turn={lastTurn}
          live={!!live}
          textEmpty={!text.trim()}
          canContinue={conv.canContinue}
          outboxEmpty={!conv.outbox.length}
          disabled={conv.busy}
          onPick={pickNextStep}
        />
        {/* שדה הכתיבה במקום אחד בכל רגע: כשבלוק מוגדל — בתחתית ההגדלה */}
        {!conv.canContinue ? (
          <p className="m-0 text-muted-foreground">{T.ai.cannotContinue}</p>
        ) : empty ? (
          <div className="flex min-h-[calc(var(--ab-screen)-14rem)] flex-col items-center justify-center gap-4 md:min-h-[calc(var(--ab-screen)-2rem)]" data-testid="empty-conversation">
            <p className="m-0 text-2xl font-semibold" aria-hidden="true">
              {T.ai.emptyTitle}
            </p>
            {composer(false, true)}
            <div className="flex flex-wrap justify-center gap-2" data-testid="starters">
              {(level === 1 ? T.ai.starters1 : T.ai.starters2).map((q) => (
                <button
                  key={q}
                  type="button"
                  className="min-h-11 cursor-pointer rounded-sm border border-border bg-card px-3 text-sm text-foreground transition-colors hover:border-primary hover:text-primary pointer-fine:min-h-9"
                  onClick={() => pickNextStep(q)}
                  disabled={conv.busy}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          !expanded && composer(false)
        )}
        {/* סמן הסוף — אחרי שדה הכתיבה (sticky), כדי שההצמדה תחשוף את התוכן מעליו ולא תשאיר אותו מתחת לשדה */}
        <div ref={stick.endRef} aria-hidden="true" />
      </section>

      {desktop &&
        (panelRail ? (
          <div className="sticky top-4 flex flex-col items-center" data-testid="panel-rail">
            <button type="button" onClick={() => setPanelOpen(true)} aria-label={T.ai.openPanel} title={T.ai.panel} aria-expanded={panelOpen} className={railBtn} data-testid="open-panel">
              <IconPanel size={20} />
            </button>
          </div>
        ) : (
          <aside className="sticky top-4 flex max-h-[calc(var(--ab-screen)-2rem)] flex-col gap-3 overflow-y-auto pe-1" aria-label={T.ai.panel}>
            {attention > 0 && (
              <p className="m-0 flex items-center gap-2 text-sm font-semibold text-muted-foreground" data-testid="panel-head">
                <span>{T.ai.attention(attention)}</span>
                {attentionBadge}
              </p>
            )}
            <div data-testid="panel">{panelBody}</div>
          </aside>
        ))}
      {(!desktop || listRail) && (
        <Sheet open={listOpen} onClose={() => setListOpen(false)} title={T.ai.conversations} side="start" className="max-w-[min(28rem,calc(100vw-2rem))] p-3">
          <ConversationList level={level} current={conversationId} variant="drawer" />
        </Sheet>
      )}
      {(!desktop || panelRail) && (
        <Sheet open={panelOpen} onClose={() => setPanelOpen(false)} title={T.ai.panel}>
          <div className="flex items-center border-b border-border p-3">
            <h2 className="m-0 me-auto text-lg font-bold">{T.ai.panel}</h2>
            <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)}>
              {T.ai.closePanel}
            </Button>
          </div>
          <div className="p-3" data-testid="panel">
            {panelBody}
          </div>
        </Sheet>
      )}
    </div>
  );
}
